/**
 * Positions ai-product-label elements on top of product gallery on mobile
 * Listens to resize events to handle orientation changes
 */

(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 749; // matches the CSS breakpoint

  /**
   * Check if we're on mobile
   */
  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /**
   * Find the product gallery (media-gallery element) for a given label
   */
  function findProductGallery(label) {
    // Try to find the media-gallery element in the same product-information section
    const productInfo = label.closest('.product-information');
    if (productInfo) {
      const gallery = productInfo.querySelector('media-gallery');
      if (gallery) return gallery;
    }
    
    // Fallback: search for the nearest media-gallery in the same section
    const section = label.closest('section');
    if (section) {
      return section.querySelector('media-gallery');
    }
    
    // Last resort: find any media-gallery on the page (for product pages)
    if (document.body.classList.contains('template-product') || window.location.pathname.includes('/products/')) {
      return document.querySelector('media-gallery');
    }
    
    return null;
  }

  /**
   * Position label on top of gallery (mobile)
   */
  function positionLabelOnGallery(label, gallery) {
    if (!gallery) return;

    // Store original position data
    if (!label.dataset.originalPosition) {
      label.dataset.originalPosition = label.style.position || '';
      label.dataset.originalTop = label.style.top || '';
      label.dataset.originalRight = label.style.right || '';
      label.dataset.originalLeft = label.style.left || '';
      label.dataset.originalMargin = label.style.margin || '';
      label.dataset.originalZIndex = label.style.zIndex || '';
      
      // Store original parent for potential restoration
      const originalParent = label.parentElement;
      if (originalParent) {
        // Create a placeholder text node to mark the position
        const placeholder = document.createTextNode('');
        originalParent.insertBefore(placeholder, label.nextSibling);
        label.dataset.originalPlaceholder = placeholder;
      }
    }
    
    // Position absolutely on top of gallery, top right corner
    label.style.position = 'absolute';
    label.style.top = '12px'; // Small offset from top
    label.style.right = '12px'; // Small offset from right
    label.style.left = 'auto';
    label.style.margin = '0';
    label.style.zIndex = '10';
    
    // Make sure the gallery container is positioned relatively
    const galleryStyle = getComputedStyle(gallery);
    if (galleryStyle.position === 'static') {
      gallery.style.position = 'relative';
      if (!gallery.dataset.originalPosition) {
        gallery.dataset.originalPosition = 'static';
      }
    }
    
    // Append label to gallery if not already a child
    if (label.parentElement !== gallery) {
      gallery.appendChild(label);
    }
  }

  /**
   * Restore original position (desktop)
   */
  function restoreOriginalPosition(label) {
    if (!label.dataset.originalPosition) return;

    // Restore original styles
    label.style.position = label.dataset.originalPosition || '';
    label.style.top = label.dataset.originalTop || '';
    label.style.right = label.dataset.originalRight || '';
    label.style.left = label.dataset.originalLeft || '';
    label.style.margin = label.dataset.originalMargin || '';
    label.style.zIndex = label.dataset.originalZIndex || '';

    // Restore gallery position if we changed it
    const gallery = label.closest('media-gallery');
    if (gallery && gallery.dataset.originalPosition === 'static') {
      gallery.style.position = '';
      delete gallery.dataset.originalPosition;
    }

    // Try to restore to original parent if placeholder exists
    if (label.dataset.originalPlaceholder) {
      try {
        const placeholder = label.dataset.originalPlaceholder;
        if (placeholder && placeholder.parentElement && label.parentElement !== placeholder.parentElement) {
          placeholder.parentElement.insertBefore(label, placeholder);
          placeholder.remove();
          delete label.dataset.originalPlaceholder;
        }
      } catch (e) {
        // If restoration fails, that's okay - CSS will handle desktop positioning
      }
    }
  }

  /**
   * Handle positioning for all labels
   */
  function handleLabelPositioning() {
    const labels = document.querySelectorAll('[class*="ai-product-label"]');
    
    labels.forEach(label => {
      const gallery = findProductGallery(label);
      
      if (isMobile() && gallery) {
        positionLabelOnGallery(label, gallery);
      } else {
        restoreOriginalPosition(label);
      }
    });
  }

  /**
   * Debounce function to limit resize event frequency
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced resize handler
  const debouncedHandlePositioning = debounce(handleLabelPositioning, 150);

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Initial positioning
    handleLabelPositioning();

    // Listen to resize events
    window.addEventListener('resize', debouncedHandlePositioning);
    
    // Listen to orientation change (for mobile devices)
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure layout has updated
      setTimeout(handleLabelPositioning, 100);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also handle dynamic content (e.g., when sections are loaded via AJAX)
  // Listen for custom events that might indicate content changes
  document.addEventListener('shopify:section:load', handleLabelPositioning);
  document.addEventListener('shopify:section:reorder', handleLabelPositioning);
})();
